import type { ReactElement } from 'react';
import { useState } from 'react';
import { Switch, Tooltip } from '@hotjar/hotsauce';
import { getMfaSettings } from '@hotjar/webapp/domains/mfa/api';
import { useCurrentUser } from '@hotjar/user';
import { EnforceSiteMfaModal } from '@hotjar/webapp/features/Mfa/EnforceSiteMfaModal/EnforceSiteMfaModal';
import { UnenforceSiteMfaModal } from '@hotjar/webapp/features/Mfa/UnenforceSiteMfaModal/UnenforceSiteMfaModal';
import { UpdateSecurityDetailsModal } from '@hotjar/webapp/features/Mfa/UpdateSecurityDetails/UpdateSecurityDetailsModal';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Site } from '@hotjar/site';
import { extractSiteGroupingProps } from '@hotjar/webapp/tracking/utils';
import { UiPosition, Event } from '@hotjar/webapp/features/Mfa/events';
import { useGate } from '@hotjar/allowances';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';

interface EnforceSiteMfaSwitchProps {
  site: Pick<
    Site,
    | 'id'
    | 'effectivePlan'
    | 'effectiveAskPlan'
    | 'enforcedMfaAt'
    | 'url'
    | 'organization'
    | 'account'
  >;
  isSSOEnabled: boolean;
}

const TRANSLATION_DOMAIN = 'account:sites-and-organizations.site';

export const EnforceSiteMfaSwitch = ({
  site,
  isSSOEnabled,
}: EnforceSiteMfaSwitchProps): ReactElement => {
  const [activateEnforce2Fa, setActivateEnforce2Fa] = useState<boolean>(false);
  const [deactivateEnforce2Fa, setDeactivateEnforce2Fa] = useState<boolean>(false);
  const [updateSecurityDetails, setUpdateSecurityDetails] = useState<boolean>(false);
  const { click } = useTrack();
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { data: user } = useCurrentUser();

  const { hasPermission } = useAuthorization();
  const hasMfaManagePermission = hasPermission(
    'account',
    `${site.organization.accountId}`,
    'sites-manage-mfa-enforcement',
  );

  const isFeatureAllowed =
    useGate('core.site_enforce_mfa', { id: String(site.id), type: 'site' }) ?? false;
  const is2FAEnforceAllowed = isFeatureAllowed && hasMfaManagePermission && !isSSOEnabled;

  let disabledMessage = t('2fa-disabled');
  if (isFeatureAllowed && !hasMfaManagePermission) {
    disabledMessage = t('2fa-site-not-owned');
  } else if (isSSOEnabled) {
    disabledMessage = t('2fa-sso-disabled');
  }

  const on2FASwitchChange = (isChecked: boolean): void => {
    if (user && isChecked) {
      click(
        Event.TOGGLE_ENFORCE_2FA_SWITCH,
        UiPosition.ENFORCE_2FA_SWITCH,
        extractSiteGroupingProps(site),
      );
      getMfaSettings(user.id).then((mfaSettings) => {
        if (mfaSettings.enabled) {
          setActivateEnforce2Fa(true);
        } else {
          setUpdateSecurityDetails(true);
        }
      });
    }

    if (user && !isChecked) {
      click(
        Event.TOGGLE_UNENFORCE_2FA_SWITCH,
        UiPosition.ENFORCE_2FA_SWITCH,
        extractSiteGroupingProps(site),
      );

      setDeactivateEnforce2Fa(true);
    }
  };

  return (
    <>
      <Tooltip isDisabled={is2FAEnforceAllowed} text={disabledMessage}>
        <span data-testid={`enforce-2fa-switch-${site.id}`}>
          <Switch
            isChecked={!!site.enforcedMfaAt}
            isDisabled={!is2FAEnforceAllowed}
            onChange={on2FASwitchChange}
            iconName={site.enforcedMfaAt ? 'lock' : 'lock_open'}
          />
        </span>
      </Tooltip>

      <UnenforceSiteMfaModal
        siteId={site.id}
        opened={deactivateEnforce2Fa}
        onClose={() => setDeactivateEnforce2Fa(false)}
      />

      <EnforceSiteMfaModal
        siteId={site.id}
        siteUrl={site.url}
        opened={activateEnforce2Fa}
        onClose={() => setActivateEnforce2Fa(false)}
      />

      <UpdateSecurityDetailsModal
        opened={updateSecurityDetails}
        onClose={() => setUpdateSecurityDetails(false)}
      />
    </>
  );
};
